import { runInnerFunction2 } from '../../../compatManager';
import { FileDrive_Funcs, globalFileDriveState } from '../../FileDrive/filedriveFuncs';
import PortalService from '../services/portal.service';
import { ConsentFormProgress } from '../types';
import { createSharePointConfig } from '../../../../../share/utils';
import { store } from '../../../redux/store';
// ========================================
// Type Definitions
// ========================================

interface ConsentFormWorkflowParams {
  portalId: string | number;
  appId: string;
  activityCenter: any;
  DataReaderObj: any;
  portalService: PortalService;
  // Progress callback for loading states
  onProgressUpdate?: (message: string) => void;
}

interface FileDriveConnection {
  drive: any;
  currentFolder: any;
}

interface InitializeConnectionParams {
  config: any;
  appId: string;
  activityCenter: any;
  onProgressUpdate?: (message: string) => void;
}

interface FindOrGenerateParams {
  currentFolder: any;
  matterNumber: string;
  onProgressUpdate?: (message: string) => void;
}

interface UploadConsentFormParams {
  pdfBlob: Blob;
  portalId: string | number;
  appId: string;
  portalService: PortalService;
}

// ========================================
// Constants
// ========================================

/**
 * Maps province short codes to their respective consent form document type IDs
 */
const CONSENT_FORM_DOCTYPE_BY_PROVINCE: Record<string, string> = {
  'SK': '2124',
  'MB': '1925',
  'BC': '5111',
  'AB': '1648',
  'NS': '1261',
  'ON': '2480',
};

export const INFO_SHARING_CONSENT_FORM_CONFIG = {
  DOCUMENT_FULL_NAME: 'Client Informed Consent Acknowledgment',
  DOC_GENERATION_MODE: 'unlock',
  DOC_NAME_SHORT: 'consent.pdf',
  DOC_FOLDER_NAME: 'Consent',
} as const;

// ========================================
// Main Workflow Orchestrator
// ========================================

/**
 * Prepare consent form (find or generate document, convert to PDF)
 * This phase does NOT upload to portal - it only prepares the PDF blob
 * 
 * @param params - Workflow parameters (portalId not needed for this phase)
 * @returns PDF blob ready for upload
 * @throws Error if any step fails
 */
export const prepareConsentFormDocument = async ({
  appId,
  activityCenter,
  DataReaderObj,
  onProgressUpdate
}: Omit<ConsentFormWorkflowParams, 'portalId' | 'portalService'>): Promise<Blob> => {
  try {
    onProgressUpdate?.(ConsentFormProgress.INITIALIZING);
    
    // Build FileDrive configuration (retrieves all values from Redux store)
    const fileDriveConfig = buildFileDriveConfig(appId);

    // Step 1: Initialize FileDrive connection
    const { currentFolder } = await initializeFileDriveConnection({
      config: fileDriveConfig,
      appId,
      activityCenter,
      onProgressUpdate
    });
    
    // Step 2: Retrieve and validate matter number
    const matterNumber = retrieveMatterNumber(DataReaderObj);
    
    // Step 3: Find existing consent form or generate new one
    const consentFile = await findOrGenerateConsentForm({
      currentFolder,
      matterNumber,
      onProgressUpdate
    });
    
    // Step 4: Convert document to PDF blob
    onProgressUpdate?.(ConsentFormProgress.UPLOADING_TO_CLIENTCONNECT);
    const pdfBlob = await convertDocumentToPdfBlob(consentFile);
    
    return pdfBlob;
    
  } catch (error) {
    console.error('Consent form preparation failed:', error);
    throw error;
  }
};

/**
 * Upload prepared consent form PDF to portal
 * This should be called AFTER portal creation with the correct portalId
 * 
 * @param pdfBlob - Prepared PDF blob
 * @param portalId - Portal ID to upload to
 * @param appId - Application ID
 * @param portalService - Portal service instance
 */
export const uploadPreparedConsentForm = async (
  pdfBlob: Blob,
  portalId: string | number,
  appId: string,
  portalService: PortalService
): Promise<void> => {
  await uploadConsentFormToPortal({
    pdfBlob,
    portalId,
    appId,
    portalService
  });
};

// ========================================
// Helper Functions
// ========================================

/**
 * Find consent form document in matter folder using generic FileDrive methods
 * @param currentFolder - FileDrive folder to search from
 * @param matterNumber - Matter number (folder name) to search in
 * @returns Consent form document if found, null otherwise
 */
async function findConsentFormDocument(
  currentFolder: any, 
  matterNumber: string
): Promise<any> {
  try {
    const clientFolder = await currentFolder.getFolder(matterNumber, { create: false });
    
    const files = await clientFolder.getContentList();
    
    const consentFile = files.find((file: any) => {
      return file.name && 
             file.name.toLowerCase().includes('client informed consent acknowledgment - clientconnect');
    });
    
    return consentFile || null;
  } catch (e) {
    console.error(`Error finding consent form with matterNumber ${matterNumber}:`, e);
    return null;
  }
}

/**
 * Poll for document with exponential backoff
 * @param currentFolder - FileDrive folder to search in
 * @param matterNumber - Matter number to search for
 * @param maxAttempts - Maximum number of polling attempts (default: 10)
 * @returns Document if found, null otherwise
 */
export const waitForDocument = async (currentFolder: any, matterNumber: string, maxAttempts = 10): Promise<any> => {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    
    const consentFile = await findConsentFormDocument(currentFolder, matterNumber);
    
    if (consentFile) {
      return consentFile;
    }
    const backoff = Math.pow(1.5, attempt);
    if (attempt < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, backoff * 1000));
    }
  }
  
  return null;
};

/**
 * Build FileDrive configuration object
 * Retrieves all required values from Redux store
 * @param appId - Application ID
 * @returns FileDrive config object
 */
function buildFileDriveConfig(appId: string) {
  // Get all required values from Redux store
  const sessionInfo = store?.getState()?.SessionInfo;
  const provinceCode = sessionInfo?.appProvinceShort; // appProvinceShort is a short form of a province name such as ON, AB, BC, etc.
  const sid = sessionInfo?.sid;
  const firmId = sessionInfo?.firm?.id?.toString();  // firmId is the integer identifier of a firm/organizational account. userId is used interchangeably with firmId in the codebase
  
  const sharepointConfig = createSharePointConfig({
    appId,
    provinceCode, 
    firmId, 
    sessionId: sid,
    endpoint: process.env.REACT_APP_RWAPI_ENDPOINT || 'rwapi',
  });
  
  return {
    type: 1,
    sharepointConfig
  };
}

/**
 * Initialize or retrieve existing FileDrive connection
 * @param params - Connection parameters including pre-built config
 * @returns Drive and current folder
 */
async function initializeFileDriveConnection(params: InitializeConnectionParams): Promise<FileDriveConnection> {
  const { config, appId, activityCenter, onProgressUpdate } = params;
  let drive, currentFolder;
  
  if (globalFileDriveState.drive && globalFileDriveState.currentFolder) {
    onProgressUpdate?.(ConsentFormProgress.USING_EXISTING_FILEDRIVE_CONNECTION);

    drive = globalFileDriveState.drive;
    currentFolder = globalFileDriveState.currentFolder;
  } else {
    onProgressUpdate?.(ConsentFormProgress.CREATING_NEW_FILEDRIVE_CONNECTION);
    
    drive = FileDrive_Funcs.createLddDrive(config, appId, undefined, undefined, activityCenter);
    currentFolder = drive.root;
  }
  
  return { drive, currentFolder };
}

/**
 * Extract and validate matter number from data reader
 * @param dataReader - RPWDataReader instance
 * @returns Matter number
 * @throws Error if matter number cannot be retrieved
 */
function retrieveMatterNumber(dataReader: any): string {
  const matterNumber = dataReader?.getFileNumber();
  if (!matterNumber) {
    throw new Error('Could not retrieve matter number (file number)');
  }
  return matterNumber;
}

/**
 * Get the appropriate consent form DOCTYPE_ID for the current province
 * Retrieves province from Redux store
 * @returns DOCTYPE_ID string for the current province
 * @throws Error if province is not configured or DOCTYPE not found
 */
function getConsentFormDoctypeId(): string {
  const sessionInfo = store?.getState()?.SessionInfo;
  const provinceCode = sessionInfo?.appProvinceShort;
  
  if (!provinceCode) {
    throw new Error('Province code not available in session');
  }
  
  const doctypeId = CONSENT_FORM_DOCTYPE_BY_PROVINCE[provinceCode];
  
  if (!doctypeId) {
    throw new Error(`No consent form DOCTYPE configured for province: ${provinceCode}`);
  }
  
  return doctypeId;
}

/**
 * Trigger consent form document generation in legacy ASP application
 * If the user's save to option is MyDrive, the generated document will be save to their MyDrive
 * If the user's save to option is Cloud, it will be saved to their sharepoint drive if its configured already
 * If the user's save to option is Download or Network, it will be saved to the computer or their network drive if they are configured already
 * Triggering this inner function will also update the newly generated document name to the <Document> node of the file XML 
 */
function generateConsentFormDocument(): void {
  const mockElement = document.createElement('div');
  const doctypeId = getConsentFormDoctypeId(); // Get province-specific DOCTYPE_ID
  
  runInnerFunction2('PreviewLinkedIssueDocument', [
    mockElement,
    doctypeId,
    INFO_SHARING_CONSENT_FORM_CONFIG.DOCUMENT_FULL_NAME,
    null,
    null,
    INFO_SHARING_CONSENT_FORM_CONFIG.DOC_GENERATION_MODE
  ]);
}

/**
 * Find existing consent form or generate a new one
 * @param params - Search and generation parameters
 * @returns Consent form document
 * @throws Error if document cannot be found or generated
 */
async function findOrGenerateConsentForm(params: FindOrGenerateParams): Promise<any> {
  const { currentFolder, matterNumber, onProgressUpdate } = params;
  let consentFile = await findConsentFormDocument(currentFolder, matterNumber);
  
  if (!consentFile) {
    // No existing document found - generate new one
    onProgressUpdate?.(ConsentFormProgress.GENERATING_DOCUMENT);
    generateConsentFormDocument();
    
    // Wait for document generation using polling with progressive backoff delay and retries up to 10 times
    onProgressUpdate?.(ConsentFormProgress.WAITING_FOR_DOCUMENT_GENERATION);
    consentFile = await waitForDocument(currentFolder, matterNumber);
    
    if (!consentFile) {
      throw new Error('Consent form was not generated or not found in SharePoint');
    }
  }
  
  return consentFile;
}

/**
 * Convert FileDrive document to PDF blob
 * @param consentFile - Consent form document from FileDrive
 * @returns PDF blob
 * @throws Error if conversion fails
 */
async function convertDocumentToPdfBlob(consentFile: any): Promise<Blob> {
  const pdfBlob = await FileDrive_Funcs.getDocumentAsPDFBlob(consentFile);
  if (!pdfBlob) {
    throw new Error('Failed to get PDF blob from document');
  }
  return pdfBlob;
}

/**
 * Upload consent form PDF to Azure portal storage
 * @param params - Upload parameters
 */
async function uploadConsentFormToPortal(params: UploadConsentFormParams): Promise<void> {
  const arrayBuffer = await params.pdfBlob.arrayBuffer();
  await params.portalService.uploadDocument(
    arrayBuffer,
    params.portalId.toString(),
    params.appId,
    INFO_SHARING_CONSENT_FORM_CONFIG.DOC_NAME_SHORT,
    INFO_SHARING_CONSENT_FORM_CONFIG.DOC_FOLDER_NAME
  );
}